import { memo } from 'react'
import clsx from 'clsx'
import { motion } from 'motion/react'
import { FlipCard } from 'ui/flip-card'

import { DRAG_SCALE } from './const'
import { useDraggableFlipCard } from './hooks'
import type { DraggableFlipCardProps } from './types'

export const DraggableFlipCard = memo(
  ({
    id = 'draggable',
    innerContainerClassName,
    interactive = true,
    exitDelta,
    onCardUnknown,
    onCardSnoozed,
    onFadingEnd,
    onFadingStart,
    className,
    children,
    ...props
  }: DraggableFlipCardProps) => {
    const {
      handleClick,
      handleDrag,
      handleDragEnd,
      handleDragStart,
      handleKeyDown,
      isDragEnabled,
      isDragging,
      motionStyle,
    } = useDraggableFlipCard({
      exitDelta,
      interactive,
      onCardSnoozed,
      onCardUnknown,
      onFadingEnd,
      onFadingStart,
    })

    return (
      <motion.div
        className={clsx('h-full touch-manipulation', className)}
        drag={isDragEnabled}
        dragMomentum={false}
        id={id}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        style={motionStyle}
        whileDrag={{ scale: DRAG_SCALE }}
      >
        <FlipCard
          className="h-full"
          innerContainerClassName={clsx('border', innerContainerClassName)}
          {...props}
          aria-pressed={isDragging || undefined}
          aria-roledescription="draggable"
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={interactive ? 0 : -1}
        >
          {children}
        </FlipCard>
      </motion.div>
    )
  },
)

DraggableFlipCard.displayName = 'DraggableFlipCard'
